import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, bookingApi, unwrap, type ApiEnvelope } from '@/lib/api/client';
import type { BookingLocation } from '@/features/memberships/types';
import type { PagedGifts } from './types';

const get = <T>(url: string) => unwrap<T>(bookingApi.get<ApiEnvelope<T>>(url));

/**
 * Gift search.
 *
 * `SearchSubscriptionGiftsBindingModel` — camelCase, and `status` must be
 * omitted rather than sent as -1 to mean "any".
 */
export interface GiftSearchInput {
  searchTerm: string;
  status: number | null;
  page: number;
  size: number;
  locationId: string | null;
  startDate: string;
  endDate: string;
}

export function useGiftSearch(input: GiftSearchInput, enabled = true) {
  return useQuery({
    queryKey: ['gifts', 'search', input],
    queryFn: () =>
      unwrap<PagedGifts>(
        bookingApi.post('subscriptions/gifts/search', {
          searchTerm: input.searchTerm,
          status: input.status,
          page: input.page,
          size: input.size,
          locationId: input.locationId,
          startDate: input.startDate,
          endDate: input.endDate,
        })
      ),
    enabled,
    placeholderData: keepPreviousData,
  });
}

export function useBookingLocations() {
  return useQuery({
    queryKey: ['booking', 'locations'],
    queryFn: () => get<BookingLocation[]>('locations'),
    staleTime: 5 * 60 * 1000,
  });
}

/** `AssignSubscriptionGiftBindingModel`. */
export function useAssignGift() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: {
      giftId: string;
      recipientUserId: string;
      comments?: string | null;
    }) => unwrap<unknown>(bookingApi.post('subscriptions/gifts/assign', input)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['gifts'] });
    },
  });
}

/** User lookup for the assign modal — the main API, not the booking one. */
export function useUserSearch(term: string, enabled: boolean) {
  return useQuery({
    queryKey: ['users', 'search', term],
    queryFn: () =>
      unwrap<{
        results: {
          id: string;
          firstName: string;
          lastName: string;
          completeName?: string;
          email: string;
          avatar?: string | null;
        }[];
      }>(api.post('users/search', { SearchText: term })),
    enabled: enabled && term.trim().length > 0,
  });
}
